import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isNil } from 'lodash-es';
import { formatPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import {
  ABOVE_MARKET,
  DEAL_COVERAGE,
  FAIR_DEAL,
  GOOD_DEAL,
  GREAT_DEAL,
  HIGH_DEAL,
} from 'site-modules/shared/components/deal/deal-constants';
import { isTexas } from 'site-modules/shared/utils/inventory-utils/is-texas';
import { getEspTrustDate } from 'client/site-modules/shared/utils/esp';

import './price-checker-edmunds-advice.scss';

const DEAL_TYPE_ALLOWED_VALUES = [GREAT_DEAL, GOOD_DEAL, FAIR_DEAL, HIGH_DEAL, ABOVE_MARKET];
const LEASE_FALLBACK_DEAL_TYPES = [GREAT_DEAL, GOOD_DEAL, FAIR_DEAL, HIGH_DEAL];
const LEASE_FALLBACK_COMPARISONS = {
  [GREAT_DEAL]: 'below',
  [GOOD_DEAL]: 'below',
  [FAIR_DEAL]: 'around',
  [HIGH_DEAL]: 'above',
};

function getEdmundsDecision(guaranteedPrice, bestDealPrice, dealType) {
  if (guaranteedPrice > bestDealPrice && bestDealPrice) {
    return (
      <span>
        is only <strong>{formatPriceStringV2(guaranteedPrice - bestDealPrice)} above</strong>
      </span>
    );
  }

  if (guaranteedPrice < bestDealPrice && bestDealPrice) {
    return (
      <span>
        is <strong>{formatPriceStringV2(bestDealPrice - guaranteedPrice)} below</strong>
      </span>
    );
  }

  if (!bestDealPrice && dealType === GREAT_DEAL) {
    return (
      <span>
        is <strong>well below</strong>
      </span>
    );
  }

  if (!bestDealPrice && dealType === GOOD_DEAL) {
    return (
      <span>
        is <strong>below</strong>
      </span>
    );
  }

  if (!bestDealPrice && dealType === FAIR_DEAL) {
    return (
      <span>
        is <strong>around</strong>
      </span>
    );
  }

  return 'is the same as';
}

function getLeasePaymentComparison(inputValue, bestDealPrice) {
  const roundedInputValue = Math.round(inputValue);
  const roundedBestDealPrice = Math.round(bestDealPrice);

  const isBelow = roundedInputValue < roundedBestDealPrice;
  const difference = Math.abs(roundedBestDealPrice - roundedInputValue);
  const direction = isBelow ? 'below' : 'above';

  if (roundedInputValue === roundedBestDealPrice) {
    return (
      <Fragment>
        is the same as <strong>the Edmunds suggested payment</strong>
      </Fragment>
    );
  }

  return (
    <Fragment>
      is{' '}
      <strong>
        {formatPriceStringV2(difference)} {direction} the Edmunds suggested payment
      </strong>
    </Fragment>
  );
}

function getLeasePaymentFallback(dealType) {
  const comparison = LEASE_FALLBACK_COMPARISONS[dealType];

  if (!comparison) {
    return null;
  }

  return (
    <Fragment>
      is <strong>{comparison} what others are paying for this vehicle</strong>
    </Fragment>
  );
}

function getLeasePaymentDecision(inputValue, bestDealPrice, dealType) {
  if (!bestDealPrice) {
    return getLeasePaymentFallback(dealType);
  }

  const suggestedPaymentCopy = ` of ${formatPriceStringV2(bestDealPrice)}/mo based on current market value`;

  if (dealType === GREAT_DEAL && inputValue < bestDealPrice) {
    return (
      <Fragment>
        {getLeasePaymentComparison(inputValue, bestDealPrice)}
        {suggestedPaymentCopy}
      </Fragment>
    );
  }

  if (dealType === GOOD_DEAL) {
    return (
      <Fragment>
        {getLeasePaymentComparison(inputValue, bestDealPrice)}
        {suggestedPaymentCopy}
      </Fragment>
    );
  }

  if (dealType === FAIR_DEAL && inputValue > bestDealPrice) {
    return (
      <Fragment>
        is only <strong>{formatPriceStringV2(inputValue - bestDealPrice)} above the Edmunds suggested payment</strong>
        {suggestedPaymentCopy}
      </Fragment>
    );
  }

  if (dealType === HIGH_DEAL) {
    return (
      <Fragment>
        is <strong>well above the Edmunds suggested payment</strong>
        {suggestedPaymentCopy}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {getLeasePaymentComparison(inputValue, bestDealPrice)}
      {suggestedPaymentCopy}
    </Fragment>
  );
}

function getLeasePaymentAdvice(inputValue, bestDealPrice, dealType) {
  return (
    <Fragment>
      because the estimated lease payment
      {!isNil(inputValue) && ` (${formatPriceStringV2(inputValue)}/mo.)`}{' '}
      {getLeasePaymentDecision(inputValue, bestDealPrice, dealType)}.
    </Fragment>
  );
}

const getDealerTextInput = (inputValue, dealerPrice, forceLease) => {
  if (dealerPrice && inputValue === dealerPrice) {
    return forceLease ? 'Based on dealer price' : 'Dealer';
  }

  return '';
};

const getDealerPriceCopy = (dealerTextInput, forceLease, defaultCopy) => {
  if (!dealerTextInput) {
    return defaultCopy;
  }

  if (forceLease) {
    return dealerTextInput;
  }

  return `${dealerTextInput} ${defaultCopy}`;
};

export function PriceCheckerEdmundsAdvice({
  bestDealPrice = null,
  inputValue = null,
  dealType = '',
  dealerPrice = null,
  showAdvice = false,
  isUsed = false,
  stateCode = '',
  wrapperClassname = 'price-checker-advice',
  showLeasePayments = false,
  transactionCountCopyValues = null,
  forceLease = false,
  totalRebate = null,
}) {
  const dealTypeName = dealType.charAt(0).toUpperCase() + dealType.slice(1);

  if (
    !(dealTypeName && DEAL_TYPE_ALLOWED_VALUES.includes(dealTypeName)) ||
    (!(dealerPrice || inputValue) && !showAdvice) ||
    (showLeasePayments && !bestDealPrice && !LEASE_FALLBACK_DEAL_TYPES.includes(dealTypeName))
  ) {
    return null;
  }

  const isTexasState = isTexas(stateCode);
  const decision = isTexasState ? (
    ''
  ) : (
    <Fragment>
      {' '}
      because the {showLeasePayments ? 'lease payment' : 'price'}{' '}
      {getEdmundsDecision(inputValue, bestDealPrice, dealTypeName)} <strong>market</strong>
    </Fragment>
  );
  const ourRatingsCopy =
    'Our ratings are accurate and up-to-date – we’ve analyzed thousands of similar transactions and listings to rate these deals.';
  let secondPartNew =
    dealTypeName === HIGH_DEAL
      ? ourRatingsCopy
      : `${
          DEAL_COVERAGE.new[dealTypeName]
        } of the new inventory currently listed on Edmunds is a ${dealType} price. ${ourRatingsCopy}`;

  if (transactionCountCopyValues) {
    const { group, level } = transactionCountCopyValues;
    secondPartNew = `Our price ratings are based on ${group} sales in ${level} up through ${getEspTrustDate()}.`;
  }

  const whatPayCopy =
    transactionCountCopyValues && !isUsed && bestDealPrice >= dealerPrice
      ? `what others are paying for this vehicle${totalRebate ? ', with selected incentives applied' : ''}`
      : `what Edmunds suggests you pay for this vehicle${totalRebate ? ', with selected incentives applied' : ''}`;

  const dealerTextInput = getDealerTextInput(inputValue, dealerPrice, forceLease);
  const dealerPriceCopy = getDealerPriceCopy(dealerTextInput, forceLease, 'Price');
  const highDealerPriceCopy = getDealerPriceCopy(dealerTextInput, forceLease, 'price');

  const copy = isUsed ? (
    <Fragment>
      Edmunds considers this used {isTexasState && dealTypeName !== FAIR_DEAL ? 'car' : 'vehicle'} a{' '}
      <span>
        <strong>{dealTypeName} Price</strong>
      </span>
      {dealTypeName === FAIR_DEAL ? (
        <Fragment>
          {' '}
          because the {showLeasePayments ? 'lease payment' : 'price'} <strong>is within the market average</strong> for
          similar vehicles in your region
        </Fragment>
      ) : (
        decision
      )}
      .
      {dealTypeName === FAIR_DEAL
        ? ''
        : ` ${
            DEAL_COVERAGE.used[dealTypeName]
          } of the used inventory currently listed on Edmunds is a ${dealType} price.`}{' '}
      {ourRatingsCopy}
    </Fragment>
  ) : (
    <Fragment>
      Edmunds considers this a{' '}
      <span>
        <strong>{dealTypeName} Price</strong>
      </span>{' '}
      {showLeasePayments ? (
        <Fragment>
          {getLeasePaymentAdvice(inputValue, bestDealPrice, dealTypeName)} {secondPartNew}
        </Fragment>
      ) : (
        <Fragment>
          because the {dealerPriceCopy}
          {!!bestDealPrice && ` (${formatPriceStringV2(inputValue)})`}{' '}
          {getEdmundsDecision(inputValue, bestDealPrice, dealTypeName)} <strong>{whatPayCopy}</strong>
          {!!bestDealPrice && ` (${formatPriceStringV2(bestDealPrice)})`}. {secondPartNew}
        </Fragment>
      )}
    </Fragment>
  );

  const getUsedHighDealCopy = () => (
    <Fragment>
      Edmunds considers this used vehicle a <strong>High Price</strong> because the {highDealerPriceCopy} is{' '}
      <strong>above the average list price </strong>
      for similar vehicles in your region. {ourRatingsCopy}
    </Fragment>
  );

  const getNewHighDealCopy = () => (
    <Fragment>
      Edmunds considers this a <strong>High Price</strong> because the {highDealerPriceCopy} is{' '}
      <strong>
        well above
        {` ${whatPayCopy}`}
      </strong>
      . {secondPartNew}
    </Fragment>
  );

  const getHighDealLeaseCopy = () => (
    <Fragment>
      Edmunds considers this a <strong>High Price</strong>{' '}
      {getLeasePaymentAdvice(inputValue, bestDealPrice, dealTypeName)} {secondPartNew}
    </Fragment>
  );

  function renderContent() {
    if (dealTypeName !== HIGH_DEAL) {
      return copy;
    }
    if (showLeasePayments) {
      return getHighDealLeaseCopy();
    }
    return isUsed ? getUsedHighDealCopy() : getNewHighDealCopy();
  }

  return (
    <div
      className={classNames(
        'bg-gradient rounded-12 p-1',
        dealType,
        wrapperClassname,
        'text-gray-darker mx-auto mt-1_25 mt-md-1_5'
      )}
      aria-live="polite"
      aria-atomic="true"
    >
      {!!inputValue && (
        <span className="visually-hidden">
          {formatPriceStringV2(inputValue)}
          {showLeasePayments ? '/mo.' : ''}
        </span>
      )}
      {renderContent()}
    </div>
  );
}

PriceCheckerEdmundsAdvice.propTypes = {
  bestDealPrice: PropTypes.number,
  inputValue: PropTypes.number,
  dealType: PropTypes.string,
  dealerPrice: PropTypes.number,
  showAdvice: PropTypes.bool,
  isUsed: PropTypes.bool,
  stateCode: PropTypes.string,
  wrapperClassname: PropTypes.string,
  showLeasePayments: PropTypes.bool,
  transactionCountCopyValues: PropTypes.shape({
    group: PropTypes.string,
    level: PropTypes.string,
  }),
  forceLease: PropTypes.bool,
  totalRebate: PropTypes.number,
};
